/**
 * Search results SKU hover tooltip.
 *
 * Shows a product's SKU in a small tooltip that follows the cursor while the
 * merchant hovers a search result card. The SKU is intentionally NOT printed
 * on the card itself (so customers don't ask about it) — it only appears on
 * hover, as a private lookup aid for the shop owner.
 *
 * Accessibility / scope:
 * - Only initialised for fine pointers (mouse). Touch devices get nothing,
 *   so the feature is effectively desktop-only and never blocks mobile taps.
 * - The tooltip is decorative and supplemental: it is aria-hidden, never takes
 *   pointer or keyboard focus (pointer-events: none) and can be dismissed with
 *   Escape. It conveys no information required to use the page, so it does not
 *   reduce accessibility for keyboard or screen reader users.
 */
(function searchSkuTooltip() {
  if (window.__searchSkuTooltip) return;

  // Mouse-only: skip touch / coarse pointers entirely.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (!finePointer.matches) return;

  window.__searchSkuTooltip = true;

  const OFFSET_X = 16;
  const OFFSET_Y = 18;
  const EDGE = 8;
  const CARD_SELECTOR = '.product-grid__item[data-sku]';
  const PILL_SELECTOR = '.blade-lengths__pill[data-variant-sku]';

  const tooltip = document.createElement('div');
  tooltip.className = 'search-sku-tooltip';
  tooltip.setAttribute('role', 'presentation');
  tooltip.setAttribute('aria-hidden', 'true');
  tooltip.hidden = true;
  document.body.appendChild(tooltip);

  let activeCard = null;
  let activeSku = null;
  let pointerX = 0;
  let pointerY = 0;
  let frame = null;
  let tooltipWidth = 0;
  let tooltipHeight = 0;

  /**
   * The card's own data-sku is its default variant's. A blade-length pill
   * under the cursor names a specific variant, so that variant's SKU is the
   * one worth showing while the cursor is on it.
   *
   * elementsFromPoint rather than event.target: the card's overlay link sits
   * above the content in its own stacking context and wins every hit-test, so
   * a pill is never the event target (the same reason blade-lengths.js has to
   * resolve its own clicks and hover this way).
   */
  function skuAt(card, x, y) {
    if (typeof document.elementsFromPoint === 'function' && card.querySelector(PILL_SELECTOR)) {
      const pill = document.elementsFromPoint(x, y).find((el) => el.matches?.(PILL_SELECTOR));
      // Containment check: elementsFromPoint is viewport-wide, so without it a
      // pill overlapping a neighbouring card could rewrite the wrong tooltip.
      if (pill && card.contains(pill)) return pill.getAttribute('data-variant-sku');
    }
    return card.getAttribute('data-sku');
  }

  // One rAF does both jobs: re-resolving which SKU is under the cursor and
  // moving the tooltip. Keeping the elementsFromPoint call on this frame budget
  // (rather than firing it per pointermove) is what keeps a dense grid cheap.
  function update() {
    frame = null;
    if (!activeCard) return;

    const sku = skuAt(activeCard, pointerX, pointerY);
    if (!sku) {
      hide();
      return;
    }
    if (sku !== activeSku) {
      activeSku = sku;
      tooltip.textContent = sku;
      // Measured only when the text changes, not on every frame.
      ({ width: tooltipWidth, height: tooltipHeight } = tooltip.getBoundingClientRect());
    }

    const width = tooltipWidth;
    const height = tooltipHeight;
    let x = pointerX + OFFSET_X;
    let y = pointerY + OFFSET_Y;

    // Flip / clamp so the tooltip stays inside the viewport.
    if (x + width + EDGE > window.innerWidth) x = pointerX - width - OFFSET_X;
    if (y + height + EDGE > window.innerHeight) y = pointerY - height - OFFSET_Y;

    tooltip.style.transform = `translate(${Math.max(EDGE, x)}px, ${Math.max(EDGE, y)}px)`;
  }

  function schedule() {
    if (frame === null) frame = requestAnimationFrame(update);
  }

  function hide() {
    if (!activeCard) return;
    activeCard = null;
    activeSku = null;
    tooltip.hidden = true;
  }

  function show(card) {
    const sku = skuAt(card, pointerX, pointerY);
    if (!sku) {
      hide();
      return;
    }
    activeCard = card;
    activeSku = null; // update() sets the text and measures it once visible
    tooltip.hidden = false;
    update();
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    pointerX = event.clientX;
    pointerY = event.clientY;
    const target = event.target;
    const card = target instanceof Element ? target.closest(CARD_SELECTOR) : null;
    if (card) {
      if (card !== activeCard) show(card);
    } else if (activeCard) {
      hide();
    }
  });

  document.addEventListener('pointermove', (event) => {
    if (!activeCard) return;
    if (event.pointerType && event.pointerType !== 'mouse') return;
    pointerX = event.clientX;
    pointerY = event.clientY;
    // Also re-resolves the SKU: moving between pills inside one card changes
    // which variant is under the cursor without ever changing the active card.
    schedule();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') hide();
  });

  window.addEventListener('scroll', hide, { passive: true });
  document.documentElement.addEventListener('pointerleave', hide);
})();
